/**
 * Product With Purchase (PWP) popup management
 * Controls display timing and carousel functionality for promotional product offers
 */

import Flickity from 'flickity'
import { VisibilityElement } from '../custom-elements/visibility'
import { formatMoney } from './currency-formatter'
import { UnifiedItem } from './cart.types'

export class PWP {
  static PRODUCT_WITH_PURCHASE_ID = 'product-with-purchase'
  static PRODUCT_WITH_PURCHASE_SELECTOR = '[data-id="product-with-purchase"]'
  static PRODUCT_WITH_PURCHASE_SESSION_STORAGE_KEY = 'pwp-display-history'
  static CAROUSEL_SELECTOR = '.flickity-element'
  static ADDED_ITEM_SELECTOR = '[data-pwp-added-item]'
  /** Rendered box is 110px mobile / 180px desktop — 360 covers 2x retina at the larger size. */
  static ADDED_IMAGE_WIDTH = 360

  /**
   * Asks the Shopify CDN for a resized variant of an image URL.
   *
   * Cart line items expose the unresized master, but this block renders it at
   * 110-180px — without a width param the browser downloads and decodes the full
   * master for a thumbnail. The `image.liquid` snippet isn't usable here because
   * the src is assigned at runtime from the cart response rather than by Liquid.
   */
  static resizeCdnImage(url: string, width: number): string {
    if (!url) return url
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`
  }

  /**
   * Populates the "recently added to cart" block inside the PWP modal with the
   * image, title and price of the line item that was just added to the cart.
   */
  static populateAddedItem(item?: Partial<UnifiedItem>): void {
    const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)
    if (!productWithPurchase || !item) return

    const container = productWithPurchase.querySelector<HTMLElement>(PWP.ADDED_ITEM_SELECTOR)
    if (!container) return

    const image = container.querySelector<HTMLImageElement>('[data-pwp-added-image]')
    const title = container.querySelector<HTMLElement>('[data-pwp-added-title]')
    const price = container.querySelector<HTMLElement>('[data-pwp-added-price]')

    const imageUrl = item.featured_image?.url || item.image
    if (image && imageUrl) {
      image.src = PWP.resizeCdnImage(imageUrl, PWP.ADDED_IMAGE_WIDTH)
      image.alt = item.featured_image?.alt || item.product_title || item.title || ''
    }
    if (title) {
      title.textContent = item.product_title || item.title || ''
    }
    if (price) {
      const amount = item.final_price ?? item.price
      if (typeof amount === 'number') price.textContent = formatMoney(amount)
    }

    container.classList.remove('hidden')
    container.classList.add('flex')
  }

  static showProductWithPurchase(addedItem?: Partial<UnifiedItem>): boolean {
    const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)

    // If PWP element doesn't exist or there's no global PWP data, exit early
    if (!productWithPurchase || !window.globals?.metaobjects?.product_with_purchase) {
      return false
    }

    const pwpData = window.globals.metaobjects.product_with_purchase
    const retriggerDelay = pwpData.retrigger_delay

    // Get last display time from session storage
    let lastDisplayed: Date | null = null
    try {
      const storageData = localStorage.getItem(PWP.PRODUCT_WITH_PURCHASE_SESSION_STORAGE_KEY)
      if (storageData) {
        lastDisplayed = new Date(storageData)
      }
    } catch (error) {
      console.error('Error parsing PWP display history:', error)
    }

    const now = new Date()

    // Check if we should show the PWP based on retrigger delay
    let shouldShow = true

    if (retriggerDelay > 0 && lastDisplayed) {
      // Calculate hours since last display
      const hoursSinceLastDisplay = Math.floor((now.valueOf() - lastDisplayed.valueOf()) / (1000 * 60 * 60))
      // Only show if enough hours have passed since last display
      shouldShow = hoursSinceLastDisplay >= retriggerDelay
    }

    if (shouldShow) {
      // Populate the recently added cart item before revealing the modal
      PWP.populateAddedItem(addedItem)

      // Show the PWP
      VisibilityElement.dispatchShow(PWP.PRODUCT_WITH_PURCHASE_ID)

      // Update display history with current timestamp
      localStorage.setItem(PWP.PRODUCT_WITH_PURCHASE_SESSION_STORAGE_KEY, now.toISOString())

      PWP.reloadFlickity()
      return true
    }

    return false
  }

  static reloadFlickity() {
    const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)
    if (!productWithPurchase) return
    const carousel = productWithPurchase.querySelector(PWP.CAROUSEL_SELECTOR)
    if (carousel) {
      Flickity.data(carousel).resize()
    }
  }
}
